import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { calculateLevodopaTestFromDocuments } from 'neuro-calculation-commons';
import { exists } from 'neuro-utils';
import { Item } from 'Components/Grid';
import HistoryRowControls from 'Components/HistoryRowControls';
import HistoryRowListing from 'Components/HistoryRowListing';
import HistorySection from 'Components/HistorySection';
import InfoPopover from 'Components/InfoPopover';
import DocumentCreationButton from 'Components/DocumentCreationButton';
import DescriptionText from 'Routes/Updrs/utils/DescriptionText';

import { getUPDRSIIIDoc } from 'Routes/Updrs/utils';
import type { TRelevantDocumentType } from 'Routes/Updrs/utils';

import { updrs_iii } from 'neuro-schemas';
import { mapDocumentToNeuroDocument } from 'Utility/documentHandling';

const getCalculatedScore = (
  doc: IDocumentWithTypeV2<'levodopatest'>,
  allDocuments: INeuroDocWithData[],
): number | null | undefined => {
  const thisDocument = mapDocumentToNeuroDocument(doc);
  return calculateLevodopaTestFromDocuments(thisDocument, allDocuments);
};

const Levodopatest = ({ documents, allDocuments, startEdit, view }: IOwnProps): React.JSX.Element => {
  // Memoize the mapped "allDocuments" to avoid re-mapping them for every row render
  const _allDocuments = React.useMemo(() => allDocuments.map(d => mapDocumentToNeuroDocument(d)), [allDocuments]);

  return (
    <HistorySection
      headerText={<FormattedMessage id={'updrs.levodopatest'} />}
      newButton={
        <DocumentCreationButton
          name="levodopatest"
          text={'updrs.newLevodopatest'}
          onClick={startEdit({}, 'levodopatest')}
          width={15}
          height={3}
          fontSize={14}
          alternate={true}
          creationConfirmationText={
            documents.length > 0 &&
            !(exists(getCalculatedScore(documents[0] ?? {}, _allDocuments)) || exists(documents[0]?.manualScore)) ? (
              <FormattedMessage id="updrs.completeOrCreateNew" />
            ) : undefined
          }
          creationConfirmationButtons={{
            cancel: {
              callback: () => startEdit(documents[0] ?? {}, 'levodopatest'),
              text: <FormattedMessage id="updrs.complete" />,
            },
            confirm: { text: 'updrs.createNew' },
          }}
        />
      }
      headerInfoText={<DescriptionText type={'levodopatest'} />}
    >
      <HistoryRowListing
        documents={documents}
        headers={
          <>
            <Item xs={4}>
              <FormattedMessage id={'updrs.updrsIIIBefore'} />
            </Item>
            <Item xs={4}>
              <FormattedMessage id={'updrs.updrsIIIAfter'} />
            </Item>
            <Item xs={4}>
              <FormattedMessage id={'updrs.levodopatestScore'} />
            </Item>
          </>
        }
        contentFormat={d => {
          const updrsIIIBeforeDoc = getUPDRSIIIDoc(d['updrsIIIBeforeID'], allDocuments);
          const updrsIIIAfterDoc = getUPDRSIIIDoc(d['updrsIIIAfterID'], allDocuments);

          const effectScore = getCalculatedScore(d, _allDocuments);

          const beforeDocHasManualScore =
            updrsIIIBeforeDoc?.retrospectiveData?.[0] === true && typeof updrsIIIBeforeDoc.manualScore === 'number';
          const afterDocHasManualScore =
            updrsIIIAfterDoc?.retrospectiveData?.[0] === true && typeof updrsIIIAfterDoc.manualScore === 'number';

          const beforeScore = updrsIIIBeforeDoc && updrs_iii.calculateScore(updrsIIIBeforeDoc);
          const afterScore = updrsIIIAfterDoc && updrs_iii.calculateScore(updrsIIIAfterDoc);

          return (
            <>
              <Item xs={4}>
                {typeof beforeScore === 'number' ? (
                  <div>
                    {beforeScore}{' '}
                    <FormattedMessage
                      id={`updrs.${beforeDocHasManualScore ? (d.testType === 'UPDRS' ? 'manualScale' : 'mdsScale') : 'manualScale'}`}
                    />
                  </div>
                ) : (
                  <InfoPopover text="general.notFilled" color="primary" />
                )}
              </Item>
              <Item xs={4}>
                {typeof afterScore === 'number' ? (
                  <div>
                    {afterScore}{' '}
                    <FormattedMessage
                      id={`updrs.${afterDocHasManualScore ? (d.testType === 'UPDRS' ? 'manualScale' : 'mdsScale') : 'manualScale'}`}
                    />
                  </div>
                ) : (
                  <InfoPopover text="general.notFilled" color="primary" />
                )}
              </Item>
              <Item xs={4}>
                {typeof effectScore === 'number'
                  ? Math.round(effectScore * 100) / 100 + ' %'
                  : exists(d.manualScore)
                    ? d.manualScore + ' %'
                    : '-'}
              </Item>
            </>
          );
        }}
        makeDate
        makeControls
        historyRowControls={d => (
          <HistoryRowControls
            document={d}
            startEdit={startEdit}
            view={exists(getCalculatedScore(d, _allDocuments)) || exists(d.manualScore) ? view : undefined}
            subDocs={[
              d.updrsIIIAfterID ? { name: 'updrs_iii', id: d.updrsIIIAfterID } : undefined,
              d.updrsIIIBeforeID ? { name: 'updrs_iii', id: d.updrsIIIBeforeID } : undefined,
            ]}
          />
        )}
      />
    </HistorySection>
  );
};

interface IOwnProps {
  documents: IDocumentWithTypeV2<'levodopatest'>[];
  allDocuments: IDocumentWithTypeV2<TRelevantDocumentType>[];
  startEdit: StartEdit;
  view?: IView;
}

export default Levodopatest;
